import {
  WorkspaceContentSearchDocument,
  WorkspaceContentSearchFolder,
} from "@/models/workspace/WorkspaceContentSearch";

/** The documents of one workspace that share a folder; an empty path is the workspace root. */
export interface ContentSearchFolderGroup {
  /** Stable key for React lists: the folder ids joined, or "root". */
  key: string;
  folderPath: WorkspaceContentSearchFolder[];
  documents: WorkspaceContentSearchDocument[];
  /** At least one of the documents has an outdated search index, the flag the stale-index notice counts. */
  hasStaleIndex: boolean;
}

const ROOT_KEY = "root";

const folderKey = (folderPath: WorkspaceContentSearchFolder[]): string =>
  folderPath.length === 0 ? ROOT_KEY : folderPath.map((f) => f.id).join("/");

/** The items `first` picks, then the rest; both keep their order, so the ranking survives. */
const partition = <T>(items: T[], first: (item: T) => boolean): T[] => [
  ...items.filter(first),
  ...items.filter((item) => !first(item)),
];

/**
 * Groups one workspace's hits by the folder that holds them, so the result view can draw
 * the staircase folder → document (LAR-235). Hits with an outdated search index come
 * first at both levels: folders that hold one, and those documents inside their folder.
 * That puts what the stale-index notice counts right under it. Otherwise groups appear in
 * the order their first document appears, and documents keep their order inside a group.
 * Both follow the server's ranking, so the best hit of each tier stays on top.
 */
export const buildContentSearchTree = (
  documents: WorkspaceContentSearchDocument[]
): ContentSearchFolderGroup[] => {
  const groups: ContentSearchFolderGroup[] = [];
  const byKey = new Map<string, ContentSearchFolderGroup>();

  for (const document of documents) {
    const key = folderKey(document.folderPath);
    let group = byKey.get(key);
    if (!group) {
      group = {
        key,
        folderPath: document.folderPath,
        documents: [],
        hasStaleIndex: false,
      };
      byKey.set(key, group);
      groups.push(group);
    }
    group.documents.push(document);
    if (document.searchIndexStale) group.hasStaleIndex = true;
  }

  for (const group of groups) {
    group.documents = partition(group.documents, (doc) => doc.searchIndexStale);
  }
  return partition(groups, (group) => group.hasStaleIndex);
};
